import { RunState } from './runState';
import { SeededRandom } from './seededRandom';

export type UpgradeId =
  | 'range'
  | 'damage'
  | 'attack-speed'
  | 'move-speed'
  | 'max-hp'
  | 'pickup-range'
  | 'crit-chance'
  | 'scatter-shot'
  | 'blood-contract'
  | 'treasure-map'
  | 'magnet-ritual'
  | 'sniper-oath'
  | 'war-drum'
  | 'survival-pact'
  | 'crit-engine';

export interface UpgradeOption {
  id: UpgradeId;
  name: string;
  description: string;
  color: number;
}

export type UpgradeRewardKind = 'heal-orb' | 'xp-orb' | 'haste-orb';

export const UPGRADE_OPTIONS: readonly UpgradeOption[] = [
  {
    id: 'range',
    name: '射程',
    description: '伙伴射程 +15%',
    color: 0x8ad8ff,
  },
  {
    id: 'damage',
    name: '伤害',
    description: '伙伴伤害 +10%',
    color: 0xffc857,
  },
  {
    id: 'attack-speed',
    name: '攻速',
    description: '伙伴攻速 +8%',
    color: 0xf5e56b,
  },
  {
    id: 'move-speed',
    name: '移速',
    description: '移动速度 +8%',
    color: 0x66b8ff,
  },
  {
    id: 'max-hp',
    name: '生命',
    description: '最大生命 +12',
    color: 0xff7f6e,
  },
  {
    id: 'pickup-range',
    name: '拾取',
    description: '拾取范围 +20%',
    color: 0x7adf8d,
  },
  {
    id: 'crit-chance',
    name: '暴击',
    description: '暴击率 +5%',
    color: 0xff5d73,
  },
  {
    id: 'scatter-shot',
    name: '散射训练',
    description: '伤害、攻速、射程一起小幅提升。',
    color: 0xffd86f,
  },
  {
    id: 'blood-contract',
    name: '血契',
    description: '大幅增伤和暴击，但最大生命下降。',
    color: 0xd94b5d,
  },
  {
    id: 'treasure-map',
    name: '藏宝图',
    description: '立刻获得材料，并扩大拾取范围。',
    color: 0xf0b84f,
  },
  {
    id: 'magnet-ritual',
    name: '磁暴仪式',
    description: '拾取范围大幅提升，移动更轻快。',
    color: 0x62e6d4,
  },
  {
    id: 'sniper-oath',
    name: '狙击誓约',
    description: '射程和伤害提高，但攻速略降。',
    color: 0x8ad8ff,
  },
  {
    id: 'war-drum',
    name: '战鼓',
    description: '攻速明显提升，移动速度小幅提升。',
    color: 0xf58a4b,
  },
  {
    id: 'survival-pact',
    name: '应急包',
    description: '最大生命提升，并立刻恢复生命。',
    color: 0x7adf8d,
  },
  {
    id: 'crit-engine',
    name: '暴击引擎',
    description: '暴击率和攻速一起提升。',
    color: 0xff6fa8,
  },
];

export class UpgradeSystem {
  private readonly rng: SeededRandom;

  constructor(seed: string) {
    this.rng = new SeededRandom(`${seed}:upgrade`);
  }

  pickOptions(count = 4) {
    if (!Number.isInteger(count) || count < 1 || count > UPGRADE_OPTIONS.length) {
      throw new Error(`Invalid upgrade option count: ${count}`);
    }

    const pool = [...UPGRADE_OPTIONS];
    const options: UpgradeOption[] = [];
    while (options.length < count) {
      const index = this.rng.integer(0, pool.length - 1);
      const [option] = pool.splice(index, 1);
      options.push(option);
    }
    return options;
  }

  apply(runState: RunState, option: UpgradeOption) {
    runState.applyUpgrade(option.id);
  }

  rollUpgradeReward(chance: number): UpgradeRewardKind | null {
    if (!Number.isFinite(chance) || chance < 0 || chance > 1) {
      throw new Error(`Invalid upgrade reward chance: ${chance}`);
    }
    if (this.rng.next() >= chance) {
      return null;
    }

    const roll = this.rng.next();
    if (roll < 0.5) return 'heal-orb';
    if (roll < 0.8) return 'xp-orb';
    return 'haste-orb';
  }

  pickInstantUpgrade() {
    return this.pickOptions(1)[0];
  }
}
